import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Star } from "lucide-react";
import { motion } from "framer-motion";

export default function PerformanceForm({ employees, onSubmit, onCancel, isLoading }) {
  const [formData, setFormData] = useState({
    employee_id: "",
    employee_name: "",
    review_period: "",
    overall_rating: 0,
    strengths: "",
    areas_for_improvement: "",
    goals: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "employee_id") {
      const employee = employees.find(emp => emp.id === value);
      if (employee) {
        setFormData(prev => ({
          ...prev,
          employee_name: `${employee.first_name} ${employee.last_name}`
        }));
      }
    }
  };

  const handleRatingClick = (rating) => {
    setFormData({ ...formData, overall_rating: rating });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="mb-8"
    >
      <Card className="border-none shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">New Performance Review</h2>
            <Button variant="ghost" size="icon" onClick={onCancel}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="employee_id">Select Employee</Label>
                <select
                  id="employee_id"
                  name="employee_id"
                  value={formData.employee_id}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-gray-300"
                  required
                >
                  <option value="">Choose an employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.position}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="review_period">Review Period</Label>
                <Input
                  id="review_period"
                  name="review_period"
                  placeholder="e.g., Q1 2024"
                  value={formData.review_period}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Overall Rating</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleRatingClick(rating)}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        rating <= formData.overall_rating
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="strengths">Strengths</Label>
              <Textarea
                id="strengths"
                name="strengths"
                value={formData.strengths}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="areas_for_improvement">Areas for Improvement</Label>
              <Textarea
                id="areas_for_improvement"
                name="areas_for_improvement"
                value={formData.areas_for_improvement}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="goals">Goals for Next Period</Label>
              <Textarea
                id="goals"
                name="goals"
                value={formData.goals}
                onChange={handleChange}
                rows={3}
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Submit Review"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </motion.div>
  );
}
